import { Box, Grid, IconButton } from "@mui/material";
import FullScreenModal from "../../../../components/common/modalPopup/FullScreenModal";
import { Document, Page, pdfjs } from "react-pdf";
import { ClientInvoiceAgent } from "../../../../app/api/clientInvoices/ClientInvoiceAgent";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../../../components/common/loader/Loader";
import { SubmittedClientInvoice } from "../../../../app/models/responseModels/ClientInvoiceResponses";
import DownloadIcon from "@mui/icons-material/Download";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.js`;

interface SubmittedInvoicePreviewProps {
  invoice: SubmittedClientInvoice | undefined;
  closeInvoicePreviewModal: CallableFunction;
}

const SubmittedInvoicePreviewModal = (props: SubmittedInvoicePreviewProps) => {
  const [numPages, setNumPages] = useState<number>();
  const [file, setFile] = useState<any>();
  const [invoiceFileResponse, setInvoiceFileResponse] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (props.invoice) {
      fetchInvoiceFile(props.invoice.clientInvoiceAutoId, props.invoice.status);
    }
  }, []);

  const fetchInvoiceFile = async (invoicePkId: number, invoiceStatus: string) => {
    try {
      setLoading(true);
      const status = (invoiceStatus === "Client Invoiced") ? "submit" : invoiceStatus;
      const response = await ClientInvoiceAgent.fetchViewClientInvoiceFile(
        invoicePkId,
        status
      );
      setInvoiceFileResponse(response);
      setFile(new Blob([response.data]));
    } catch (error) {
      toast.error("File isn't accessible.", {
        toastId: "FetchSubmittedInvoiceFileException",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FullScreenModal
      title="Invoice Preview"
      updateAsyncOperation={async () => true}
      closeFullScreenModal={props.closeInvoicePreviewModal}
      isLoading={loading}
      closeModalAfterSubmit={true}
      confirmMessage=""
      isUpdatePermission={false}
      isDeletePermission={false}
      showSubmitButton={false}
      showDeleteButton={false}
    >
      <Box>
        {loading ? (
          <Loader isLoading={loading} />
        ) : (
          <>
            <Grid container justifyContent={"space-between"}>
              <Grid item>
                <IconButton
                  type="button"
                  sx={{ marginRight: 2 }}
                  onClick={() => {
                    const rawFileName = invoiceFileResponse.headers["file-name"];
                    const fileName = rawFileName ? rawFileName.replace(/[^a-zA-Z0-9._-]/g, "_") : "invoice.pdf";
                    const url = window.URL.createObjectURL(
                      new Blob([invoiceFileResponse.data])
                    );
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  }}
                >
                  <DownloadIcon sx={{ color: "primary.main" }} />
                </IconButton>
              </Grid>
            </Grid>
            <Box>
              <Document
                file={file}
                onLoadSuccess={({ numPages }: { numPages: number }) => {
                  setNumPages(numPages);
                }}
                onLoadError={(error) => console.error("Failed to load PDF: ", String(error))}
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <>
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={Math.max(window.innerWidth * 0.95, 400)}
                    />
                    <p>
                      Page {index + 1} of {numPages}
                    </p>
                  </>
                ))}
              </Document>
            </Box>
          </>
        )}
      </Box>
    </FullScreenModal>
  );
};

export default SubmittedInvoicePreviewModal;
